/**
 * Общий аллокатор z-index для поверхностей (драверов и модалок).
 *
 * Раньше у драверов и модалок были фиксированные базы (150 и 200), к которым
 * прибавлялся индекс внутри своего стека. Из-за этого дравер, открытый из модалки,
 * получал z-index ниже модалки и оказывался под ней.
 *
 * Здесь — единый монотонный счётчик на оба хоста: каждая новая поверхность
 * получает z-index выше предыдущей, поэтому «открытое последним» всегда сверху,
 * независимо от типа (дравер/модалка) и порядка вложенности.
 *
 * Счётчик сбрасывается до базы, когда закрывается последняя поверхность, —
 * чтобы за долгую сессию z не дорос до диапазона поповеров/тултипов Mantine.
 */

import { useEffect, useState } from 'react';

const BASE = 200;
/** Запас на внутренние слои поверхности (overlay + content). */
const STEP = 2;

let counter = 0;
/** Сколько поверхностей открыто суммарно (драверы + модалки). */
let openCount = 0;

/** Выдать z-index для новой поверхности. Вызывать только при открытии нового id. */
export function acquireZIndex(): number {
  openCount++;
  return BASE + ++counter * STEP;
}

/** Освободить z-index при закрытии поверхности. Сбрасывает счётчик, когда закрылась последняя. */
export function releaseZIndex(): void {
  if (--openCount <= 0) {
    openCount = 0;
    counter = 0;
  }
}

/**
 * Z-index для старых Mantine `<Modal opened>`, НЕ интегрированных с ModalHost/DrawerHost
 * (т.е. не через useModal/useDrawer). На время открытия выдаёт z-index из общего
 * аллокатора — поверх любой текущей поверхности; освобождает при закрытии/анмаунте.
 * Так обычная `<Modal>`, открытая из дравера, оказывается сверху, а не под ним.
 *
 * @example <Modal zIndex={useSurfaceZIndex(opened)} opened={opened} onClose={...} />
 */
export function useSurfaceZIndex(opened: boolean): number | undefined {
  const [zIndex, setZIndex] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!opened) return;
    setZIndex(acquireZIndex());
    return () => {
      releaseZIndex();
      setZIndex(undefined);
    };
  }, [opened]);
  return zIndex;
}
